import {
    ColorFilter,
    ColorFilterType,
    ConvolveImageFilter,
    FilterChain,
    ImgUtils,
    ImageFilter,
    MATRIX_GAUSSIAN_BLUR_3,
    Point,
    ImageSourceElement
} from "mini-canvas-engine";
import { DotsEditor } from "../components/dots-editor";
import { DotsGenerator } from "./dots-generator";

export enum HintImageType {
    None = 0,
    Edge = 1,
    KeepBlack = 2
}

export class HintImageGenerator {
    constructor(private dotsEditor: DotsEditor | ImageSourceElement, private dotsGenerator?: DotsGenerator) {}

    private _canvas?: HTMLCanvasElement;
    public get canvas(): HTMLCanvasElement | undefined {
        if (this.isValidImageType() && !this._canvas) {
            this.generateImage();
        }
        return this._canvas;
    }

    private _type: HintImageType = HintImageType.None;
    public get type(): HintImageType {
        return this._type;
    }
    public set type(value: HintImageType) {
        if (value != this._type) {
            this._type = value;
            // Invalidate the canvas
            this._canvas = undefined;
        }
    }

    private _hintAmount = 5;
    public get hintAmount(): number {
        return this._hintAmount;
    }
    public set hintAmount(value: number) {
        if (value != this._hintAmount) {
            this._hintAmount = value;
            // Invalidate the canvas
            this._canvas = undefined;
        }
    }

    private _lineWidth = 4; /* 4 = 20px on a 1200x1200 image */
    public get lineWidth(): number {
        return this._lineWidth;
    }
    public set lineWidth(value: number) {
        if (value != this._lineWidth) {
            this._lineWidth = value;
            // Invalidate the canvas
            this._canvas = undefined;
        }
    }

    private _onlyInside = true;
    public get onlyInside(): boolean {
        return this._onlyInside;
    }
    public set onlyInside(value: boolean) {
        if (value != this._onlyInside) {
            this._onlyInside = value;
            // Invalidate the canvas
            this._canvas = undefined;
        }
    }

    public isValidImageType() {
        return this._type == HintImageType.Edge || this._type == HintImageType.KeepBlack;
    }

    clear() {
        this._canvas = undefined;
    }

    private generateImage() {
        const image = this.getImage();
        if (!image) {
            throw new Error("No background image");
        }

        // Duplicate the canvas
        const canvas = ImgUtils.convertImageToCanvas(image, this._type == HintImageType.Edge ? 640 : undefined);

        let filter: ImageFilter;
        if (this._type == HintImageType.Edge) {
            const amountAlpha = Math.max(400 - this._hintAmount * 40, 0);
            filter = new FilterChain(canvas, [
                (canvas) => new ConvolveImageFilter(canvas, MATRIX_GAUSSIAN_BLUR_3),
                (canvas) =>
                    new ConvolveImageFilter(canvas, [
                        [-1, -1, -1],
                        [-1, 8, -1],
                        [-1, -1, -1]
                    ]),
                (canvas) =>
                    new ColorFilter(canvas, [
                        { type: ColorFilterType.Invert },
                        { type: ColorFilterType.Grayscale },
                        { type: ColorFilterType.Brightness, param: (-this._hintAmount / 15) * 127 },
                        { type: ColorFilterType.Contrast, param: (this._hintAmount / 15) * 256 },
                        { type: ColorFilterType.AlphaColor, param: { color: { r: 255, g: 255, b: 255 }, threshold: amountAlpha } }
                    ])
            ]);
        } else if (this._type == HintImageType.KeepBlack) {
            const amount = 764 - this._hintAmount * 70;
            filter = new ColorFilter(canvas, [
                { type: ColorFilterType.Contrast, param: 1.5 },
                { type: ColorFilterType.Grayscale },
                { type: ColorFilterType.AlphaColor, param: { color: { r: 255, g: 255, b: 255 }, threshold: amount } }
            ]);
        } else {
            console.warn("Unknown image hint type: " + this._type);
            return;
        }

        filter.apply();

        this.eraseContoursOfHintImage(canvas);

        this._canvas = canvas;
    }

    private getImage() {
        return this.dotsEditor instanceof DotsEditor ? this.dotsEditor.getSourceImage() : this.dotsEditor;
    }

    private eraseContoursOfHintImage(canvas: HTMLCanvasElement) {
        if (!this.dotsGenerator || !(this.dotsEditor instanceof DotsEditor)) {
            return;
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Could not get 2D context");
        }
        ctx.save();
        try {
            // Set to erase
            ctx.globalCompositeOperation = "destination-out";
            // Set thick line
            ctx.lineWidth = Math.max(Math.round((Math.max(canvas.width, canvas.height) / 240) * this._lineWidth), 1);

            const ratio = canvas.width / this.dotsGenerator.generatedWidth;

            // Draw a rect with the whole canvas with the shape inside
            this.eraseAreaWithPath(canvas, ctx, ratio, this.dotsEditor.points);

            // // Clear the shape of the original trace algorythm
            // if (this.dotsGenerator.pointsBeforeSimplify) {
            //     eraseAreaWithPath(ratio, this.dotsGenerator.pointsBeforeSimplify);
            // }
        } finally {
            ctx.restore();
        }
    }

    private eraseAreaWithPath(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, ratio: number, points: Point[]) {
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            const x = p.x * ratio;
            const y = p.y * ratio;
            if (i == 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }

        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        // Clear the shape
        ctx.stroke();

        if (this._onlyInside) {
            // Clear everything outside the shape
            ctx.closePath();
            ctx.fill("evenodd");
        }
    }
}
